/**
 * Next Best Actions Component
 * AI-powered suggestions for user's next steps based on their journey stage
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Banknote, Shield, Search, FileText, MapPin, Loader2, Sparkles, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  banknote: Banknote,
  shield: Shield,
  search: Search,
  'file-text': FileText,
  'map-pin': MapPin
};

const NextBestActions = ({ listing, userStage = 'browsing', isDemo = false }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (listing) {
      fetchNextActions();
    }
  }, [listing, userStage]);

  const fetchNextActions = async () => {
    try {
      setLoading(true);
      
      const vehicleData = {
        listing_id: listing.id || listing._id,
        price: listing.price || 0,
        type: listing.vehicle_type || listing.category || 'car',
        age: listing.vehicle_age || (listing.year ? new Date().getFullYear() - listing.year : 5),
        trust_score: listing.trust_score || 70,
        condition: listing.condition || 'used'
      };

      const response = await axios.post(`${API_URL}/api/insurance/bundle/next-actions`, {
        vehicle_data: vehicleData,
        user_stage: userStage,
        country: listing.country || 'SL'
      });

      if (response.data.success) {
        setActions(response.data.actions || []);
      }
    } catch (err) {
      console.error('Error fetching next actions:', err);
      setError('Could not load suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (action) => {
    // Strip the /listing/{id} prefix so we can treat every action URL as an anchor
    // when it points into the current listing. Backend returns:
    //   - "#services"                        (already anchor-only)
    //   - "/listing/{id}#insurance"          (full path - same page, just needs to scroll)
    //   - "/other/path"                      (real navigation)
    let target = action.url || '';
    const hashIdx = target.indexOf('#');

    // Same-page scroll if the URL has a hash and its path (if any) matches this listing
    if (hashIdx >= 0) {
      const pathPart = target.slice(0, hashIdx);
      const anchor = target.slice(hashIdx + 1);
      const currentListingId = listing.id || listing._id;
      const isSamePage = !pathPart || pathPart === '' ||
                         pathPart === window.location.pathname ||
                         pathPart.endsWith(`/${currentListingId}`);
      if (isSamePage && anchor) {
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    }

    // Otherwise do a real navigation
    if (target.startsWith('/')) {
      window.location.href = target;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-indigo-500 mr-2" />
        <span className="text-sm text-gray-500">Loading suggestions...</span>
      </div>
    );
  }

  if (error || actions.length === 0) {
    return null;
  }

  return (
    <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-testid="next-best-actions">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-indigo-600" />
          <h3 className="font-semibold text-indigo-900">Recommended Next Steps</h3>
          <Badge variant="outline" className="text-xs bg-white border-indigo-300 text-indigo-700">
            AI Powered
          </Badge>
          {isDemo && (
            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="nba-demo-badge">
              DEMO
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {actions.map((action, index) => {
            const IconComponent = ICON_MAP[action.icon] || Shield;
            
            return (
              <button
                key={action.id || index}
                onClick={() => handleActionClick(action)}
                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all text-left group"
                data-testid={`next-action-${action.id}`}
              >
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                  ${action.priority === 1 ? 'bg-indigo-100' : 'bg-gray-100'}
                `}>
                  <IconComponent className={`h-5 w-5 ${action.priority === 1 ? 'text-indigo-600' : 'text-gray-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{action.title}</p>
                  <p className="text-sm text-gray-500 truncate">{action.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-indigo-500 transition-colors flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default NextBestActions;
